import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { type FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  acceptAttribute,
  isAbortError,
  UPLOAD_RULES,
  type UploadPurpose,
  uploadFile,
  validateUploadFile,
} from '@/shared/api';
import { formatFileSize, formatPercent } from '@/shared/lib';
import { FormField } from '../form/FormField';
import {
  asList,
  type BaseFieldProps,
  describedBy,
  useBoundField,
  useFieldIds,
} from '../form/fieldBase';
import { useFormBusy } from '../form/formBusy';
import { Lightbox } from '../Lightbox';
import { UploadDropzone, type UploadedFile, UploadFileRow } from './UploadParts';
import { useUploadMessages } from './useFileUpload';

/** Файл галереи, уже сохранённый в записи. */
export interface GalleryFile extends UploadedFile {
  fileId: string;
}

export interface GalleryUploadFieldProps<TValues extends FieldValues>
  extends BaseFieldProps<TValues> {
  purpose: UploadPurpose;
  /** Файлы записи (значение при открытии формы) — для превью. */
  current?: readonly GalleryFile[];
  /** Наибольшее число файлов. */
  max: number;
  /** Номер первого файла в подписи «фото N» (например, 2 — после фото профиля). */
  numberFrom?: number;
  /** Подпись в зоне выбора. */
  note?: string;
}

interface Slot {
  key: string;
  /** file_id: у сохранённых — сразу, у новых — после `complete`. */
  fileId: string | null;
  name: string;
  previewUrl: string | null;
  progress: number | null;
  error: string | null;
  controller: AbortController | null;
}

function preview(file: File): string | null {
  if (!file.type.startsWith('image/') || typeof URL.createObjectURL !== 'function') return null;
  return URL.createObjectURL(file);
}

/**
 * Галерея изображений для форм (прототип: поле `media` с несколькими файлами — зона «Перетащите
 * файлы или выберите на диске» и список строк с миниатюрой, чипом «фото N» и «Убрать»). Значение —
 * массив `file_id` в порядке показа; новые файлы грузятся параллельно (COM-13) и встают в конец
 * в порядке выбора. Пока идёт загрузка, FormDialog не отправляет форму.
 */
export function GalleryUploadField<TValues extends FieldValues>({
  name,
  label,
  required,
  hint,
  wide = true,
  disabled,
  purpose,
  current = [],
  max,
  numberFrom = 1,
  note,
}: GalleryUploadFieldProps<TValues>) {
  const { t, i18n } = useTranslation('form');
  const ids = useFieldIds();
  const { clearErrors } = useFormContext<TValues>();
  const { field, fieldState } = useBoundField<TValues>(name);
  const { validationMessage, errorMessage } = useUploadMessages();
  const { mutateAsync } = useMutation({
    mutationFn: (variables: { file: File; signal: AbortSignal; onProgress: (p: number) => void }) =>
      uploadFile(variables.file, purpose, {
        signal: variables.signal,
        onProgress: variables.onProgress,
      }),
    meta: { errorToast: false },
  });
  const [slots, setSlots] = useState<Slot[]>(() =>
    asList(field.value).map((fileId) => {
      const known = current.find((item) => item.fileId === fileId);
      return {
        key: fileId,
        fileId,
        name: known?.name || t('upload.currentFile'),
        previewUrl: known?.url ?? null,
        progress: null,
        error: null,
        controller: null,
      };
    }),
  );
  const [lightbox, setLightbox] = useState<{ src: string; name: string } | null>(null);
  const counter = useRef(0);
  const { onChange } = field;
  useFormBusy(slots.some((slot) => slot.progress !== null));

  // Значение поля — готовые file_id в порядке строк.
  const readyIds = slots.flatMap((slot) => (slot.fileId && !slot.error ? [slot.fileId] : []));
  const readyKey = readyIds.join(',');
  const valueKey = asList(field.value).join(',');
  useEffect(() => {
    if (readyKey !== valueKey) onChange(readyKey ? readyKey.split(',') : []);
  }, [readyKey, valueKey, onChange]);

  // Прервать незавершённые загрузки при закрытии формы.
  const slotsRef = useRef(slots);
  useEffect(() => {
    slotsRef.current = slots;
  }, [slots]);
  useEffect(
    () => () => {
      for (const slot of slotsRef.current) slot.controller?.abort();
    },
    [],
  );

  function update(key: string, patch: Partial<Slot>) {
    setSlots((list) => list.map((slot) => (slot.key === key ? { ...slot, ...patch } : slot)));
  }

  function add(file: File) {
    clearErrors(name);
    counter.current += 1;
    const key = `new-${counter.current}`;
    const invalid = validateUploadFile(file, purpose);
    const controller = invalid ? null : new AbortController();
    setSlots((list) => {
      if (list.length >= max) return list;
      return [
        ...list,
        {
          key,
          fileId: null,
          name: file.name,
          previewUrl: invalid ? null : preview(file),
          progress: invalid ? null : 0,
          error: invalid ? validationMessage(invalid) : null,
          controller,
        },
      ];
    });
    if (!controller) return;
    mutateAsync({
      file,
      signal: controller.signal,
      onProgress: (progress) => update(key, { progress }),
    }).then(
      (result) => update(key, { fileId: result.upload_id, progress: null, controller: null }),
      (error: unknown) => {
        if (isAbortError(error)) return;
        update(key, { progress: null, error: errorMessage(error), controller: null });
      },
    );
  }

  function remove(key: string) {
    setSlots((list) => {
      const slot = list.find((item) => item.key === key);
      slot?.controller?.abort();
      if (slot?.previewUrl?.startsWith('blob:') && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(slot.previewUrl);
      }
      return list.filter((item) => item.key !== key);
    });
  }

  const rule = UPLOAD_RULES[purpose];
  const full = slots.length >= max;
  const error = fieldState.error?.message;
  let number = numberFrom;

  return (
    <FormField
      label={label}
      htmlFor={ids.id}
      required={required}
      counter={{ length: slots.length, max }}
      error={error}
      errorId={ids.errorId}
      hint={hint}
      hintId={ids.hintId}
      wide={wide}
    >
      <div className="flex flex-col gap-8">
        <UploadDropzone
          id={ids.id}
          multiple
          accept={acceptAttribute(purpose)}
          title={t('upload.chooseFiles')}
          note={
            note ??
            t('upload.galleryNote', {
              formats: t(`upload.formats.${rule.formats}`),
              max,
              size: formatFileSize(rule.maxSize, i18n.language),
            })
          }
          disabled={disabled || field.disabled || full}
          invalid={Boolean(error)}
          describedBy={describedBy(Boolean(error), Boolean(hint), ids)}
          onFile={add}
        />
        {slots.length > 0 ? (
          <div className="overflow-hidden rounded-16 border border-line-strong">
            {slots.map((slot) => {
              const chip =
                slot.progress !== null
                  ? formatPercent(slot.progress, i18n.language)
                  : t('upload.photoNumber', { number: number++ });
              const src = slot.previewUrl;
              return (
                <UploadFileRow
                  key={slot.key}
                  bare
                  kind="image"
                  name={slot.name}
                  previewUrl={src}
                  chip={chip}
                  progress={slot.progress}
                  error={slot.error}
                  actionLabel={slot.progress !== null ? t('upload.cancel') : t('upload.remove')}
                  onAction={() => remove(slot.key)}
                  actionDisabled={disabled}
                  onOpen={
                    src && slot.progress === null
                      ? () => setLightbox({ src, name: slot.name })
                      : undefined
                  }
                />
              );
            })}
          </div>
        ) : null}
      </div>
      {lightbox ? (
        <Lightbox
          open
          onOpenChange={(open) => {
            if (!open) setLightbox(null);
          }}
          src={lightbox.src}
          title={lightbox.name}
        />
      ) : null}
    </FormField>
  );
}
